if (!customElements.get('calista-reactions-slider')) {
  customElements.define(
    'calista-reactions-slider',
    class CalistaReactionsSlider extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('.calista-reactions__viewport');
        this.track = this.querySelector('.calista-reactions__track');
        this.slides = this.track ? this.track.querySelectorAll('.calista-reactions__slide') : [];
        this.dots = this.querySelectorAll('.calista-reactions__dots .slider-counter__link--dots');

        if (!this.viewport || !this.track || this.slides.length === 0) return;

        this.offset = 0;
        this.dragging = false;
        this.moved = false;
        this.activePointer = null;
        this.axisLock = null;
        this.startX = 0;
        this.startY = 0;
        this.startOffset = 0;
        this.lastX = 0;
        this.lastTime = 0;
        this.velocity = 0;
        this.wheelLock = false;
        this.fillRaf = 0;
        this.axisThreshold = 10;

        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onWheel = this.onWheel.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onTransitionEnd = this.onTransitionEnd.bind(this);

        this.viewport.addEventListener('pointerdown', this.onPointerDown);
        this.viewport.addEventListener('wheel', this.onWheel, { passive: false });
        this.track.addEventListener('transitionend', this.onTransitionEnd);

        this.resizeObserver = new ResizeObserver(() => this.onResize());
        this.resizeObserver.observe(this.viewport);
        this.resizeObserver.observe(this.track);

        this.dots.forEach((dot, index) => {
          dot.addEventListener('click', (event) => {
            event.preventDefault();
            this.goTo(index);
          });
        });

        this.viewport.addEventListener(
          'click',
          (event) => {
            if (!this.moved) return;
            event.preventDefault();
            event.stopPropagation();
            this.moved = false;
          },
          true
        );

        this.initVideos();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.goTo(0, false);
          });
        });
      }

      disconnectedCallback() {
        if (!this.viewport) return;
        this.viewport.removeEventListener('pointerdown', this.onPointerDown);
        this.viewport.removeEventListener('wheel', this.onWheel);
        if (this.track) this.track.removeEventListener('transitionend', this.onTransitionEnd);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        this.endPointerTracking();
        this.stopFillWatch();
      }

      initVideos() {
        var section = this.closest('.calista-reactions');
        if (!section || !('IntersectionObserver' in window)) return;

        var videos = section.querySelectorAll('video');
        if (!videos.length) return;

        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              var video = entry.target;
              if (entry.isIntersecting) {
                video.play().catch(function () {});
              } else {
                video.pause();
              }
            });
          },
          { threshold: 0.45 }
        );

        videos.forEach(function (video) {
          observer.observe(video);
        });
      }

      visibleWidth() {
        return this.viewport.clientWidth;
      }

      maxOffset() {
        return Math.max(0, this.track.scrollWidth - this.visibleWidth());
      }

      clamp(value) {
        return Math.min(this.maxOffset(), Math.max(0, value));
      }

      snapOffsets() {
        var max = this.maxOffset();
        var firstLeft = this.slides[0] ? this.slides[0].offsetLeft : 0;
        var lastIndex = this.slides.length - 1;

        // Offset 0 keeps the track's page-width left inset visible.
        // Slide N snaps so its left edge lands where slide 0 started.
        return Array.from(this.slides).map(function (slide, index) {
          var preferred = Math.max(0, slide.offsetLeft - firstLeft);

          // Last slide: scroll as far as needed so the full card + trailing
          // inset fit in the viewport.
          if (index === lastIndex) {
            preferred = Math.max(preferred, max);
          }

          return Math.min(max, preferred);
        });
      }

      nearestIndex(value) {
        var offsets = this.snapOffsets();
        var nearest = 0;
        var minDistance = Infinity;

        offsets.forEach(function (offset, index) {
          var distance = Math.abs(offset - value);
          if (distance < minDistance) {
            minDistance = distance;
            nearest = index;
          }
        });

        return nearest;
      }

      displayedOffset() {
        var transform = getComputedStyle(this.track).transform;
        if (!transform || transform === 'none') return this.offset;
        var values = transform.replace(/^matrix3d\(|^matrix\(|\)$/g, '').split(',');
        var x = transform.indexOf('matrix3d') === 0 ? values[12] : values[4];
        return Math.abs(parseFloat(x) || 0);
      }

      setOffset(value, animate) {
        this.offset = this.clamp(value);
        this.track.style.transition = animate
          ? 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)'
          : 'none';
        this.track.style.transform = 'translate3d(' + -this.offset + 'px, 0, 0)';
        if (animate) {
          this.watchDots();
        } else {
          this.updateDots();
        }
      }

      currentIndex() {
        return this.nearestIndex(this.displayedOffset());
      }

      maxIndex() {
        return Math.max(0, this.slides.length - 1);
      }

      goTo(index, animate) {
        if (animate === undefined) animate = true;
        var offsets = this.snapOffsets();
        var i = Math.min(this.maxIndex(), Math.max(0, index));
        this.setOffset(offsets[i] || 0, animate);
      }

      updateDots() {
        if (!this.dots.length) return;
        var current = this.currentIndex();
        var total = this.slides.length;
        var maxBig = 4;
        var start = 0;

        if (total > maxBig && current >= maxBig) {
          start = Math.min(current - maxBig + 1, total - maxBig);
        }

        var end = Math.min(start + maxBig, total);
        var hasNext = end < total;
        var hasPrev = start > 0;

        this.dots.forEach(function (dot, index) {
          var size = 'hidden';
          if (index >= start && index < end) {
            size = 'big';
          } else if (hasNext && index === end) {
            size = 'small';
          } else if (!hasNext && hasPrev && index === start - 1) {
            size = 'small';
          }

          dot.classList.toggle('is-small', size === 'small');
          dot.classList.toggle('is-hidden', size === 'hidden');
          dot.classList.toggle('slider-counter__link--active', index === current && size !== 'hidden');
          dot.setAttribute('aria-hidden', size === 'hidden' ? 'true' : 'false');
          dot.tabIndex = size === 'hidden' ? -1 : 0;
        });
      }

      watchDots() {
        this.stopFillWatch();
        var self = this;
        var tick = function () {
          self.updateDots();
          self.fillRaf = requestAnimationFrame(tick);
        };
        this.fillRaf = requestAnimationFrame(tick);
      }

      stopFillWatch() {
        if (this.fillRaf) {
          cancelAnimationFrame(this.fillRaf);
          this.fillRaf = 0;
        }
      }

      onTransitionEnd(event) {
        if (event.propertyName !== 'transform') return;
        this.stopFillWatch();
        this.updateDots();
      }

      onWheel(event) {
        if (this.maxOffset() <= 0) return;
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        if (Math.abs(event.deltaX) < 8) return;
        event.preventDefault();
        if (this.wheelLock) return;
        this.wheelLock = true;
        this.goTo(this.currentIndex() + (event.deltaX > 0 ? 1 : -1));
        window.setTimeout(() => {
          this.wheelLock = false;
        }, 480);
      }

      onPointerDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest('a, button, input, select, textarea, label')) return;
        if (this.maxOffset() <= 0) return;

        this.activePointer = event.pointerId;
        this.axisLock = null;
        this.dragging = false;
        this.moved = false;
        this.startX = event.clientX;
        this.startY = event.clientY;
        this.lastX = event.clientX;
        this.lastTime = Date.now();
        this.startOffset = this.displayedOffset();
        this.offset = this.startOffset;
        this.velocity = 0;

        this.viewport.addEventListener('pointermove', this.onPointerMove, { passive: false });
        this.viewport.addEventListener('pointerup', this.onPointerUp);
        this.viewport.addEventListener('pointercancel', this.onPointerUp);
      }

      onPointerMove(event) {
        if (event.pointerId !== this.activePointer) return;

        var dx = event.clientX - this.startX;
        var dy = event.clientY - this.startY;

        if (!this.axisLock) {
          if (Math.abs(dx) < this.axisThreshold && Math.abs(dy) < this.axisThreshold) return;
          this.axisLock = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';

          if (this.axisLock === 'y') {
            this.endPointerTracking();
            return;
          }

          this.dragging = true;
          this.viewport.classList.add('is-dragging');
          this.track.style.transition = 'none';
          if (this.viewport.setPointerCapture) {
            try {
              this.viewport.setPointerCapture(event.pointerId);
            } catch (error) {}
          }
        }

        if (this.axisLock !== 'x' || !this.dragging) return;

        event.preventDefault();

        var now = Date.now();
        var moveDx = event.clientX - this.lastX;
        var dt = Math.max(now - this.lastTime, 1);
        this.velocity = moveDx / dt;
        this.lastX = event.clientX;
        this.lastTime = now;

        if (Math.abs(dx) > 4) this.moved = true;
        this.setOffset(this.startOffset - dx, false);
      }

      onPointerUp(event) {
        if (event.pointerId !== this.activePointer) return;

        var wasDragging = this.dragging;
        var didMove = this.moved;
        var startOffset = this.startOffset;
        var endOffset = this.offset;
        var velocity = this.velocity;

        this.endPointerTracking();

        if (!wasDragging || !didMove) return;

        var offsets = this.snapOffsets();
        var startIndex = this.nearestIndex(startOffset);
        var flick = Math.abs(velocity) > 0.35;
        var moved = endOffset - startOffset;
        var step =
          startIndex < offsets.length - 1
            ? Math.abs(offsets[startIndex + 1] - offsets[startIndex])
            : startIndex > 0
              ? Math.abs(offsets[startIndex] - offsets[startIndex - 1])
              : 1;

        if (flick || Math.abs(moved) > step * 0.18) {
          this.goTo(startIndex + (moved > 0 ? 1 : -1));
        } else {
          this.goTo(startIndex);
        }
      }

      endPointerTracking() {
        this.viewport.removeEventListener('pointermove', this.onPointerMove);
        this.viewport.removeEventListener('pointerup', this.onPointerUp);
        this.viewport.removeEventListener('pointercancel', this.onPointerUp);

        if (this.viewport.releasePointerCapture && this.activePointer != null) {
          try {
            this.viewport.releasePointerCapture(this.activePointer);
          } catch (error) {}
        }

        this.activePointer = null;
        this.axisLock = null;

        if (this.dragging) {
          this.dragging = false;
          this.viewport.classList.remove('is-dragging');
        }
      }

      onResize() {
        var index = this.currentIndex();
        this.goTo(index, false);
      }
    }
  );
}
